<template>
  <v-main>
    <v-container fluid>
      <!-- Reminder: customer return requests waiting on the admin -->
      <div v-if="pendingCount > 0" class="draft-banner mb-3">
        <span class="draft-banner__icon"><v-icon color="white" small>mdi-keyboard-return</v-icon></span>
        <span class="draft-banner__text">
          <b>{{ pendingCount }}</b> return request{{ pendingCount === 1 ? " is" : "s are" }} waiting for approval.
        </span>
        <v-spacer></v-spacer>
        <v-btn text small color="orange darken-3" @click="showPending">View requests</v-btn>
      </div>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-keyboard-return"
                title="Sale Returns"
                subtitle="Returns from the customer app, the sales app and the counter"
                style="width: 100%"
              >
                <v-tooltip bottom v-if="can('SALE_RETURNS.CREATE')">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      fab
                      x-small
                      color="primary"
                      @click="openCreate()"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                  <span>New Sale Return</span>
                </v-tooltip>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search Return #, Order # or Customer... "
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-filter-variant"
                    clearable
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
                <v-col sm="3">
                  <v-select
                    solo
                    rounded
                    v-model="filter.source"
                    :items="sourceOptions"
                    item-text="text"
                    item-value="value"
                    label="Raised From"
                    prepend-inner-icon="mdi-cellphone"
                    clearable
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <v-simple-table v-else fixed-header height="500" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Return #</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Order #</th>
                      <th class="text-left">Customer</th>
                      <th class="text-left">Raised From</th>
                      <th class="text-right">Qty</th>
                      <th class="text-right">Value</th>
                      <th class="text-right">Refund</th>
                      <th class="text-left">Status</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="returns.length != 0">
                    <tr
                      v-for="(ret, i) in returns"
                      :key="`sr_${i}`"
                      class="clickable-row"
                      @click="viewReturn(ret)"
                    >
                      <td style="min-width: 85px">{{ (page - 1) * rowsPerPage + i + 1 }}</td>
                      <td>{{ ret.return_number }}</td>
                      <td>{{ ret.created_at | niceDate }}</td>
                      <td>{{ ret.order ? ret.order.order_no : "" }}</td>
                      <td>
                        <div>{{ userName(ret.customer) }}</div>
                        <div class="text-caption grey--text" v-if="ret.customer">{{ ret.customer.phone }}</div>
                      </td>
                      <td>{{ sourceLabels[ret.source] }}</td>
                      <td class="text-right">{{ totalQty(ret) }}</td>
                      <td class="text-right mono-figure">₹{{ ret.total_amount }}</td>
                      <td class="text-right mono-figure">
                        <span v-if="ret.status === 1">₹{{ ret.refund_amount }}</span>
                        <span v-else class="grey--text">&mdash;</span>
                      </td>
                      <td>
                        <v-chip small :color="statusColors[ret.status]" text-color="white">
                          {{ statusLabels[ret.status] }}
                        </v-chip>
                      </td>
                      <td class="text-right action_th">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="my-auto"
                              :color="ret.status === 0 ? 'orange' : 'primary'"
                              @click.stop="viewReturn(ret)"
                            >
                              <v-icon dark>{{ ret.status === 0 ? "mdi-gavel" : "mdi-eye" }}</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ ret.status === 0 ? "Review this request" : "View this return" }}</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="ret.status !== 2 && can('SALE_RETURNS.UPDATE')">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="ml-2 my-auto"
                              fab
                              x-small
                              color="primary"
                              @click.stop="openEdit(ret)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this return</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="11">
                        <AppEmptyState
                          icon="mdi-keyboard-return"
                          title="No sale returns yet"
                          message="Return requests from customers and returns taken back by sellers will show up here."
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination v-model="page" :length="paginationLength"></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- View / review a return -->
    <AppDialog
      :show.sync="showViewDialog"
      max-width="880"
      icon="mdi-keyboard-return"
      :title="viewing ? viewing.return_number : ''"
      :subtitle="viewing && viewing.order ? viewing.order.order_no : ''"
      :status-text="viewing ? statusLabels[viewing.status] : ''"
      :status-color="viewing ? dialogStatusColors[viewing.status] : 'grey'"
    >
      <template v-slot:header-actions v-if="viewing && viewing.status !== 2 && can('SALE_RETURNS.UPDATE')">
        <v-btn small outlined dark class="sr-edit-btn" @click="(showViewDialog = false), openEdit(viewing)">
          <v-icon left small>mdi-pencil-outline</v-icon> Edit
        </v-btn>
      </template>

      <div v-if="viewing" class="sr-view">
        <!-- Who / where / when / how much -->
        <div class="sr-facts">
          <div class="sr-fact">
            <v-icon class="sr-fact__icon">mdi-account-outline</v-icon>
            <div>
              <div class="sr-fact__label">Customer</div>
              <div class="sr-fact__value">{{ userName(viewing.customer) || "-" }}</div>
              <div class="sr-fact__sub" v-if="viewing.customer && viewing.customer.phone">{{ viewing.customer.phone }}</div>
            </div>
          </div>
          <div class="sr-fact">
            <v-icon class="sr-fact__icon">{{ sourceIcons[viewing.source] }}</v-icon>
            <div>
              <div class="sr-fact__label">Raised from</div>
              <div class="sr-fact__value">{{ sourceLabels[viewing.source] }}</div>
              <div class="sr-fact__sub">by {{ userName(viewing.requested_by) }}</div>
            </div>
          </div>
          <div class="sr-fact">
            <v-icon class="sr-fact__icon">mdi-calendar-blank-outline</v-icon>
            <div>
              <div class="sr-fact__label">Requested on</div>
              <div class="sr-fact__value">{{ viewing.created_at | niceDate }}</div>
              <div class="sr-fact__sub">{{ timeOf(viewing.created_at) }}</div>
            </div>
          </div>
          <div class="sr-fact">
            <v-icon class="sr-fact__icon">mdi-receipt-text-outline</v-icon>
            <div>
              <div class="sr-fact__label">Order total</div>
              <div class="sr-fact__value mono-figure">₹{{ money(viewing.order ? viewing.order.final_amount || viewing.order.total_amount : 0) }}</div>
              <div class="sr-fact__sub">{{ totalQty(viewing) }} item(s) returned</div>
            </div>
          </div>
        </div>

        <!-- Why -->
        <div class="sr-reason">
          <div class="sr-section-label">Customer's reason</div>
          <div class="sr-reason__text">“{{ viewing.reason || "No reason given" }}”</div>
        </div>

        <!-- What -->
        <div class="sr-section-label">Items being returned</div>
        <div class="sr-items">
          <div v-for="item in viewing.items" :key="`vi_${item.id}`" class="sr-item">
            <div class="sr-item__thumb">
              <img v-if="variantImage(item.product_variant)" :src="variantImage(item.product_variant)" alt="" />
              <v-icon v-else color="grey lighten-1">mdi-tshirt-crew-outline</v-icon>
            </div>
            <div class="sr-item__info">
              <div class="sr-item__name">{{ productName(item.product_variant) }}</div>
              <div class="sr-item__meta">{{ variantLabel(item.product_variant) }}</div>
            </div>
            <div class="sr-item__qty mono-figure">
              {{ item.quantity }} × ₹{{ money(item.unit_price) }}
              <v-chip v-if="item.damaged_quantity > 0" x-small color="red lighten-5" text-color="red darken-2" class="ml-1">
                {{ item.damaged_quantity }} damaged · held
              </v-chip>
            </div>
            <div class="sr-item__amount mono-figure">₹{{ money(item.line_total) }}</div>
          </div>
          <div class="sr-items__total">
            <span>Return value</span>
            <span class="mono-figure">₹{{ money(viewing.total_amount) }}</span>
          </div>
        </div>

        <!-- Already decided -->
        <div v-if="viewing.status !== 0" class="sr-outcome" :class="viewing.status === 1 ? 'sr-outcome--done' : 'sr-outcome--rejected'">
          <v-icon class="sr-outcome__icon">{{ viewing.status === 1 ? "mdi-check-circle" : "mdi-close-circle" }}</v-icon>
          <div class="sr-outcome__body">
            <div class="sr-outcome__title">
              <template v-if="viewing.status === 1">
                Refunded <span class="mono-figure">₹{{ money(viewing.refund_amount) }}</span>
                via {{ refundModeLabels[viewing.refund_mode] || "-" }}
                <span v-if="viewing.refund_reference" class="sr-outcome__ref">· Ref {{ viewing.refund_reference }}</span>
              </template>
              <template v-else>Return request rejected</template>
            </div>
            <div class="sr-outcome__meta">
              {{ viewing.status === 1 ? "Completed" : "Rejected" }} by {{ userName(viewing.processed_by) || "-" }}
              · {{ viewing.processed_at | niceDateTime }}
            </div>
            <div v-if="viewing.admin_remarks" class="sr-outcome__remarks">{{ viewing.admin_remarks }}</div>
          </div>
        </div>

        <!-- Pending: the admin decides -->
        <div v-else-if="can('SALE_RETURNS.UPDATE')" class="sr-decision">
          <div class="sr-decision__head">
            <div>
              <div class="sr-decision__title">Your decision</div>
              <div class="sr-decision__hint">
                Approving puts good items back into stock, holds damaged ones in Return Stock and records the refund.
              </div>
            </div>
          </div>

          <!-- Good pieces go back to sellable stock, damaged ones are held in Return Stock -->
          <div class="sr-field-label">Condition of the items coming back</div>
          <div class="mb-4">
            <div v-for="line in decision.items" :key="`dc_${line.id}`" class="d-flex align-center mb-2" style="gap: 12px">
              <div style="min-width: 220px">{{ line.label }}</div>
              <v-text-field
                v-model.number="line.damaged_quantity"
                type="number"
                min="0"
                :max="line.quantity"
                outlined
                dense
                hide-details
                suffix="damaged"
                style="max-width: 150px"
              ></v-text-field>
              <span class="text-caption grey--text">of {{ line.quantity }} · the rest go back to stock</span>
            </div>
          </div>

          <div class="sr-field-label">Refund to <span class="red--text">*</span></div>
          <v-btn-toggle v-model="decision.refund_mode" mandatory class="sr-modes" active-class="sr-modes__active">
            <v-btn v-for="m in refundModes" :key="m.value" :value="m.value" depressed>
              <v-icon left small>{{ refundModeIcons[m.value] }}</v-icon>
              {{ m.text }}
            </v-btn>
          </v-btn-toggle>
          <div v-if="decisionErrors.refund_mode" class="error--text text-caption mt-1">{{ decisionErrors.refund_mode[0] }}</div>

          <v-row dense class="mt-4">
            <v-col cols="12" sm="6">
              <div class="sr-field-label">Refund amount</div>
              <v-text-field
                v-model.number="decision.refund_amount"
                type="number"
                min="0"
                :max="viewing.total_amount"
                outlined
                dense
                prefix="₹"
                hide-details="auto"
                :hint="`Up to ₹${money(viewing.total_amount)}`"
                persistent-hint
              >
                <template v-slot:append>
                  <v-btn
                    x-small
                    text
                    color="primary-dark"
                    class="mt-n1"
                    :disabled="Number(decision.refund_amount) === Number(viewing.total_amount)"
                    @click="decision.refund_amount = Number(viewing.total_amount)"
                  >Full amount</v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="sr-field-label">Reference <span class="grey--text">(optional)</span></div>
              <v-text-field
                v-model="decision.refund_reference"
                outlined
                dense
                hide-details
                placeholder="UPI / transaction ID"
              ></v-text-field>
            </v-col>
            <v-col cols="12" class="mt-2">
              <div class="sr-field-label">Remarks</div>
              <v-textarea
                v-model="decision.admin_remarks"
                outlined
                dense
                rows="2"
                auto-grow
                placeholder="Note for this return. Required if you reject - the customer sees it in the app."
                hide-details="auto"
                :error-messages="decisionErrors.admin_remarks"
              ></v-textarea>
            </v-col>
          </v-row>
        </div>
      </div>

      <template v-slot:actions v-if="viewing && viewing.status === 0 && can('SALE_RETURNS.UPDATE')">
        <v-btn color="success" depressed :loading="isDeciding === 'approve'" :disabled="!!isDeciding" @click="approve">
          <v-icon left>mdi-check</v-icon>
          Approve &amp; refund ₹{{ money(decision.refund_amount || 0) }}
        </v-btn>
        <v-btn color="error" outlined class="ml-2" :loading="isDeciding === 'reject'" :disabled="!!isDeciding" @click="reject">
          <v-icon left>mdi-close</v-icon> Reject
        </v-btn>
      </template>
    </AppDialog>

    <!-- New sale return (counter return on any delivered order) / edit an existing one -->
    <AppDialog
      :show.sync="showCreateDialog"
      persistent
      max-width="950"
      icon="mdi-keyboard-return"
      :title="editing ? `Edit ${editing.return_number}` : 'New Sale Return'"
      :subtitle="!editing
        ? 'Take goods back on a delivered order - completed straight away'
        : editing.status === 1
          ? 'Completed return - only the difference in stock and refund is applied'
          : 'Return request - change it before approving'"
    >
      <v-autocomplete
        v-model="createForm.order_id"
        :disabled="!!editing"
        :items="orderOptions"
        :search-input.sync="orderSearch"
        :loading="isSearchingOrders"
        item-value="id"
        :item-text="orderLabel"
        no-filter
        outlined
        dense
        hide-no-data
        prepend-inner-icon="mdi-magnify"
        label="Find a delivered order by order # or customer name/phone"
        @change="loadReturnable"
      ></v-autocomplete>

      <section v-if="isLoadingOrder" class="py-6">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </section>
      <div v-else-if="createOrder">
        <v-alert v-if="!createCanReturn" type="info" dense text>{{ createMessage }}</v-alert>
        <v-simple-table v-else class="striped-table mb-4" dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Product</th>
                <th class="text-right">Sold</th>
                <th class="text-right">{{ editing ? "On Other Returns" : "Already Returned" }}</th>
                <th class="text-right">Price Paid / Unit</th>
                <th class="text-right" style="width: 140px">Return Qty</th>
                <th class="text-right" style="width: 130px">Damaged</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in createOrder.order_details" :key="`cd_${d.id}`">
                <td>
                  <div class="font-weight-medium">{{ productName(d.product_variant) }}</div>
                  <div class="text-caption grey--text">{{ variantLabel(d.product_variant) }}</div>
                </td>
                <td class="text-right">{{ d.quantity }}</td>
                <td class="text-right">{{ d.returned_quantity }}</td>
                <td class="text-right mono-figure">₹{{ d.unit_refund_price }}</td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="d.return_qty"
                    type="number"
                    min="0"
                    :max="d.returnable_quantity"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="d.returnable_quantity <= 0"
                    :error-messages="d.return_qty > d.returnable_quantity ? `Max ${d.returnable_quantity}` : ''"
                  ></v-text-field>
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="d.damaged_qty"
                    type="number"
                    min="0"
                    :max="Number(d.return_qty) || 0"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="!(Number(d.return_qty) > 0)"
                    :error-messages="d.damaged_qty > (Number(d.return_qty) || 0) ? `Max ${Number(d.return_qty) || 0}` : ''"
                    @input="d.damaged_touched = true"
                  ></v-text-field>
                </td>
                <td class="text-right mono-figure">₹{{ ((Number(d.return_qty) || 0) * Number(d.unit_refund_price)).toFixed(2) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <div v-if="createCanReturn" class="text-caption grey--text mb-3">
          <v-icon x-small>mdi-information-outline</v-icon>
          Good pieces go back to sellable stock; damaged ones are held in Return Stock until you move them back or
          write them off. Left untouched, a reason mentioning "damaged" or "defective" marks every piece damaged.
        </div>

        <v-row dense v-if="createCanReturn">
          <v-col cols="12">
            <v-textarea v-model="createForm.reason" outlined dense rows="2" auto-grow>
              <template v-slot:label>Reason <span style="color:#f44336;font-weight:bold;">*</span></template>
            </v-textarea>
          </v-col>
          <!-- A request's refund is decided when it is approved -->
          <template v-if="!editing || editing.status === 1">
          <v-col cols="12" md="4">
            <v-select
              v-model="createForm.refund_mode"
              :items="refundModes"
              item-text="text"
              item-value="value"
              outlined
              dense
            >
              <template v-slot:label>Refund Mode <span style="color:#f44336;font-weight:bold;">*</span></template>
            </v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="createForm.refund_amount"
              type="number"
              min="0"
              outlined
              dense
              prefix="₹"
              label="Refund Amount"
              :hint="`Return value ₹${createTotal.toFixed(2)} - leave blank to refund all of it`"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="createForm.refund_reference" outlined dense label="Reference (optional)"></v-text-field>
          </v-col>
          </template>
          <v-col cols="12" v-if="editing && editing.status === 1 && editing.refund_mode === 2">
            <div class="text-caption grey--text">
              This refund went to the customer's wallet. Lowering it (or switching to cash/online) takes the difference
              back out of their wallet - not possible if they have already spent it.
            </div>
          </v-col>
        </v-row>
      </div>

      <template v-slot:actions>
        <v-btn color="primary" depressed :loading="isCreating" :disabled="!canCreate" @click="createReturn">
          {{ editing ? "Save Changes" : "Complete Return" }}
        </v-btn>
      </template>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";

export default {
  name: "saleReturnsPage",
  components: { AppDialog, AppEmptyState, AppPageHeader },
  data() {
    return {
      filter: { search_keyword: null, status: null, source: null },
      page: 1,
      paginationLength: 0,
      count: 0,
      pendingCount: 0,
      returns: [],
      isLoading: false,
      statusOptions: [
        { text: "Requested", value: 0 },
        { text: "Completed", value: 1 },
        { text: "Rejected", value: 2 },
      ],
      sourceOptions: [
        { text: "Customer App", value: 0 },
        { text: "Sales App", value: 1 },
        { text: "Admin Panel", value: 2 },
      ],
      statusLabels: { 0: "Requested", 1: "Completed", 2: "Rejected" },
      statusColors: { 0: "orange", 1: "success", 2: "error" },
      dialogStatusColors: { 0: "warning", 1: "success", 2: "error" },
      sourceLabels: { 0: "Customer App", 1: "Sales App", 2: "Admin Panel" },
      refundModes: [
        { text: "Cash", value: 0 },
        { text: "Online (UPI / Bank)", value: 1 },
        { text: "Customer Wallet", value: 2 },
      ],
      refundModeLabels: { 0: "Cash", 1: "Online", 2: "Wallet" },
      refundModeIcons: { 0: "mdi-cash", 1: "mdi-qrcode-scan", 2: "mdi-wallet-outline" },
      sourceIcons: { 0: "mdi-cellphone", 1: "mdi-storefront-outline", 2: "mdi-monitor" },
      // view / review
      showViewDialog: false,
      viewing: null,
      decision: {},
      decisionErrors: {},
      isDeciding: null,
      // create
      showCreateDialog: false,
      editing: null, // { id, return_number, status, refund_mode } when the dialog edits a return
      editingDamaged: {}, // order_detail_id -> damaged pieces already on the return being edited
      createForm: {},
      orderSearch: null,
      orderOptions: [],
      isSearchingOrders: false,
      searchTimer: null,
      createOrder: null,
      createCanReturn: false,
      createMessage: "",
      isLoadingOrder: false,
      isCreating: false,
    };
  },
  computed: {
    createTotal() {
      if (!this.createOrder) return 0;
      return this.createOrder.order_details.reduce(
        (sum, d) => sum + (Number(d.return_qty) || 0) * Number(d.unit_refund_price),
        0
      );
    },
    canCreate() {
      if (!this.createOrder || !this.createCanReturn) return false;
      const lines = this.createOrder.order_details.filter((d) => Number(d.return_qty) > 0);
      return (
        lines.length > 0 &&
        lines.every((d) => Number(d.return_qty) <= d.returnable_quantity) &&
        !!(this.createForm.reason || "").trim() &&
        // a request being edited has no refund yet
        ((this.editing && this.editing.status === 0) || this.createForm.refund_mode !== null)
      );
    },
  },
  watch: {
    page: "getData",
    orderSearch(value) {
      clearTimeout(this.searchTimer);
      if (!value || value.length < 2) return;
      this.searchTimer = setTimeout(() => this.searchOrders(value), 350);
    },
  },
  mounted() {
    this.getData();
    // Coming from an order's "Return items" action
    if (this.$route.query.order_id) {
      this.openCreate(Number(this.$route.query.order_id));
    }
  },
  methods: {
    userName(user) {
      if (!user) return "";
      return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.user_name || "";
    },
    productName(variant) {
      return variant && variant.product ? variant.product.name : "";
    },
    // ₹ figures with Indian digit grouping and 2 decimals
    money(value) {
      return (Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    timeOf(value) {
      if (!value) return "";
      const date = new Date(value);
      return isNaN(date) ? "" : date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    },
    variantImage(variant) {
      const images = (variant && variant.product_variant_images) || [];
      return images.length && images[0].image_path ? this.mediaUrl + images[0].image_path : null;
    },
    variantLabel(variant) {
      if (!variant) return "";
      return [variant.color, variant.size].filter(Boolean).join(" - ");
    },
    totalQty(ret) {
      return (ret.items || []).reduce((sum, i) => sum + Number(i.quantity), 0);
    },
    orderLabel(order) {
      const customer = this.userName(order.customer);
      return `${order.order_no}${customer ? " - " + customer : ""} (₹${order.final_amount || order.total_amount})`;
    },
    showPending() {
      this.filter.status = 0;
      this.page = 1;
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      const params = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        search_keyword: this.filter.search_keyword || "",
        status: this.filter.status === null || this.filter.status === undefined ? "" : this.filter.status,
        source: this.filter.source === null || this.filter.source === undefined ? "" : this.filter.source,
      });
      await axios
        .get(`sale_returns?${params.toString()}`)
        .then((response) => {
          this.returns = response.data.data;
          this.count = response.data.count;
          this.pendingCount = response.data.pending_count || 0;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    viewReturn(ret) {
      this.viewing = ret;
      this.decision = {
        refund_mode: ret.order && ret.order.payment_mode === 1 ? 1 : 0,
        refund_amount: Number(ret.total_amount),
        refund_reference: "",
        admin_remarks: "",
        // Starts from the request (a "damaged / defective" reason marks every piece damaged)
        items: (ret.items || []).map((i) => ({
          id: i.id,
          quantity: i.quantity,
          damaged_quantity: i.damaged_quantity || 0,
          label: `${this.productName(i.product_variant)} - ${this.variantLabel(i.product_variant)}`,
        })),
      };
      this.decisionErrors = {};
      this.showViewDialog = true;
    },
    async approve() {
      if (this.decision.refund_mode === null || this.decision.refund_mode === undefined) {
        this.decisionErrors = { refund_mode: ["Choose how the refund is paid."] };
        return;
      }
      this.isDeciding = "approve";
      await axios
        .post(`sale_returns/${this.viewing.id}/approve`, {
          ...this.decision,
          items: (this.decision.items || []).map((l) => ({
            id: l.id,
            damaged_quantity: Math.max(0, Math.min(l.quantity, Number(l.damaged_quantity) || 0)),
          })),
        })
        .then((response) => {
          this.notify("success", response.data.message || "Return approved");
          this.viewing = response.data.data;
          this.getData();
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not approve the return.");
        });
      this.isDeciding = null;
    },
    async reject() {
      if (!(this.decision.admin_remarks || "").trim()) {
        this.decisionErrors = { admin_remarks: ["Tell the customer why the return is rejected."] };
        return;
      }
      this.isDeciding = "reject";
      await axios
        .post(`sale_returns/${this.viewing.id}/reject`, { admin_remarks: this.decision.admin_remarks })
        .then((response) => {
          this.notify("success", "Return request rejected");
          this.viewing = response.data.data;
          this.getData();
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not reject the return.");
        });
      this.isDeciding = null;
    },
    openEdit(ret) {
      this.editing = {
        id: ret.id,
        return_number: ret.return_number,
        status: ret.status,
        refund_mode: ret.refund_mode,
      };
      // Damaged pieces already on this return, per order line
      this.editingDamaged = (ret.items || []).reduce((map, i) => {
        map[i.order_detail_id] = (map[i.order_detail_id] || 0) + (i.damaged_quantity || 0);
        return map;
      }, {});
      const completed = ret.status === 1;
      this.createForm = {
        order_id: ret.order_id,
        reason: ret.reason || "",
        refund_mode: completed ? ret.refund_mode : null,
        refund_amount: completed ? Number(ret.refund_amount) : null,
        refund_reference: ret.refund_reference || "",
      };
      this.createOrder = null;
      this.orderOptions = ret.order ? [{ ...ret.order, customer: ret.customer }] : [];
      this.showCreateDialog = true;
      this.loadReturnable();
    },
    openCreate(orderId = null) {
      this.editing = null;
      this.createForm = { order_id: null, reason: "", refund_mode: null, refund_amount: null, refund_reference: "" };
      this.createOrder = null;
      this.orderOptions = [];
      this.showCreateDialog = true;
      if (orderId) {
        this.createForm.order_id = orderId;
        this.loadReturnable();
      }
    },
    async searchOrders(keyword) {
      this.isSearchingOrders = true;
      await axios
        .get(`orders?order_status=4&page=1&rowsPerPage=20&search_keyword=${encodeURIComponent(keyword)}`)
        .then((response) => {
          this.orderOptions = response.data.data || [];
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isSearchingOrders = false;
    },
    async loadReturnable() {
      this.createOrder = null;
      if (!this.createForm.order_id) return;
      this.isLoadingOrder = true;
      // When editing, the API leaves this return's own lines out of "already returned"
      const exclude = this.editing ? `?exclude_return_id=${this.editing.id}` : "";
      await axios
        .get(`sale_returns/order/${this.createForm.order_id}${exclude}`)
        .then((response) => {
          const order = response.data.data;
          order.order_details = (order.order_details || []).map((d) => ({
            ...d,
            return_qty: this.editing ? d.current_quantity || 0 : 0,
            damaged_qty: this.editing ? this.editingDamaged[d.id] || 0 : 0,
            damaged_touched: !!this.editing,
          }));
          this.createOrder = order;
          this.createCanReturn = response.data.can_return;
          this.createMessage = response.data.message;
          if (!this.orderOptions.find((o) => o.id === order.id)) this.orderOptions = [order];
          if (!this.editing) this.createForm.refund_mode = order.payment_mode === 1 ? 1 : 0;
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not load the order.");
        });
      this.isLoadingOrder = false;
    },
    async createReturn() {
      this.isCreating = true;
      const payload = {
        order_id: this.createForm.order_id,
        reason: this.createForm.reason,
        refund_mode: this.createForm.refund_mode,
        refund_reference: this.createForm.refund_reference,
        items: this.createOrder.order_details
          .filter((d) => Number(d.return_qty) > 0)
          .map((d) => {
            const line = { order_detail_id: d.id, quantity: Number(d.return_qty) };
            // Untouched: the API decides from the reason ("damaged" / "defective" = all damaged)
            if (d.damaged_touched || Number(d.damaged_qty) > 0) {
              line.damaged_quantity = Math.max(0, Math.min(line.quantity, Number(d.damaged_qty) || 0));
            }
            return line;
          }),
      };
      if (this.createForm.refund_amount !== null && this.createForm.refund_amount !== "") {
        payload.refund_amount = this.createForm.refund_amount;
      }
      const request = this.editing
        ? axios.patch(`sale_returns/${this.editing.id}`, payload)
        : axios.post(`sale_returns`, payload);
      await request
        .then((response) => {
          this.showCreateDialog = false;
          this.notify(
            "success",
            this.editing
              ? response.data.message || `Sale return ${response.data.data.return_number} updated`
              : `Sale return ${response.data.data.return_number} completed - stock restored`
          );
          if (!this.editing) this.page = 1;
          this.getData();
        })
        .catch((error) => {
          this.notify("error", (error.response && error.response.data.message) || "Could not save the return.");
        });
      this.isCreating = false;
    },
    notify(type, message) {
      if (this.$toast) this.$toast[type](message);
    },
  },
};
</script>
<style scoped>
.draft-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fae8dd;
}
.draft-banner__icon {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  background: #ffa24b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.draft-banner__text {
  color: #f08a24;
  font-weight: 500;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover > td {
  background-color: #FBF7EF !important;
}

/* ─── Review dialog ─── */
.sr-edit-btn {
  border-color: rgba(255, 255, 255, 0.7) !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
.sr-view {
  color: var(--color-ink);
}
.sr-section-label,
.sr-field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(46, 42, 36, 0.6);
  margin-bottom: 8px;
}
.sr-section-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* who / where / when / how much */
.sr-facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #ece5da;
  border-radius: 12px;
  background: #fbf9f5;
  margin-bottom: 20px;
}
.sr-fact {
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  min-width: 0;
}
.sr-fact + .sr-fact {
  border-left: 1px solid #ece5da;
}
.sr-fact__icon {
  color: var(--color-primary-dark) !important;
  font-size: 20px !important;
  margin-top: 1px;
}
.sr-fact__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(46, 42, 36, 0.5);
}
.sr-fact__value {
  margin-top: 2px;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sr-fact__sub {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.6);
}

/* customer's reason */
.sr-reason {
  margin-bottom: 20px;
}
.sr-reason__text {
  padding: 12px 16px;
  border-left: 3px solid var(--color-primary);
  border-radius: 0 8px 8px 0;
  background: rgba(202, 181, 150, 0.12);
  font-size: 15px;
  font-style: italic;
}

/* items */
.sr-items {
  border: 1px solid #ece5da;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}
.sr-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
}
.sr-item + .sr-item {
  border-top: 1px solid #f1ece4;
}
.sr-item__thumb {
  flex: 0 0 44px;
  height: 52px;
  border-radius: 8px;
  background: #f3efe8;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.sr-item__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sr-item__info {
  flex: 1 1 auto;
  min-width: 0;
}
.sr-item__name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sr-item__meta {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.55);
}
.sr-item__qty {
  color: rgba(46, 42, 36, 0.65);
  font-size: 13px;
  white-space: nowrap;
}
.sr-item__amount {
  min-width: 96px;
  text-align: right;
  font-weight: 600;
}
.sr-items__total {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fbf9f5;
  border-top: 1px solid #ece5da;
  font-weight: 700;
}

/* decided */
.sr-outcome {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
}
.sr-outcome--done {
  background: rgba(76, 154, 106, 0.1);
}
.sr-outcome--done .sr-outcome__icon {
  color: var(--color-success) !important;
}
.sr-outcome--rejected {
  background: rgba(216, 86, 74, 0.08);
}
.sr-outcome--rejected .sr-outcome__icon {
  color: var(--color-error) !important;
}
.sr-outcome__title {
  font-weight: 600;
}
.sr-outcome__ref {
  font-weight: 400;
  color: rgba(46, 42, 36, 0.6);
}
.sr-outcome__meta {
  font-size: 12px;
  color: rgba(46, 42, 36, 0.6);
  margin-top: 2px;
}
.sr-outcome__remarks {
  margin-top: 8px;
  font-size: 14px;
}

/* pending: decision */
.sr-decision {
  padding: 18px 20px 6px;
  border: 1px solid #ece5da;
  border-radius: 12px;
  background: #fff;
}
.sr-decision__head {
  margin-bottom: 16px;
}
.sr-decision__title {
  font-size: 16px;
  font-weight: 700;
}
.sr-decision__hint {
  font-size: 13px;
  color: rgba(46, 42, 36, 0.6);
}
.sr-modes {
  display: flex;
  width: 100%;
  border-radius: 10px !important;
}
.sr-modes .v-btn {
  flex: 1 1 0;
  height: 42px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  background: #fff !important;
  color: rgba(46, 42, 36, 0.75) !important;
  border-color: #e3dccf !important;
}
.sr-modes .sr-modes__active {
  background: rgba(138, 111, 78, 0.12) !important;
  color: var(--color-primary-dark) !important;
}

@media (max-width: 700px) {
  .sr-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sr-fact:nth-child(3) {
    border-left: 0;
  }
  .sr-fact:nth-child(n + 3) {
    border-top: 1px solid #ece5da;
  }
  .sr-item__qty {
    display: none;
  }
}
</style>
